import {
	ColDef,
	EditableCallbackParams,
	INumberCellEditorParams,
	IRowNode,
	ValueFormatterParams,
} from 'ag-grid-community';
import { globalStyles } from 'theme/styles';
import { numberToLocaleString } from 'utils/functions/number';

import { IHeader } from '../types/agGrid.type';

const convertToNumber = (value: any, precision: number) => {
	if (typeof value === 'number') return value;
	const cleanValue = value?.replace(/\./g, '')?.replace(',', '.');
	if (isNaN(cleanValue) || cleanValue === null || cleanValue === undefined)
		return 0;
	return parseFloat(parseFloat(cleanValue).toFixed(precision));
};
type TypeCells = 'number' | 'string' | 'total';

const getSpecificColumnsByType = (
	quantityDigits: number,
	widthTotal: number
): Record<TypeCells, ColDef> => {
	const valueFormatter = (params: ValueFormatterParams<any, any>) => {
		return numberToLocaleString({
			value: +params.value || 0,
			minimumFractionDigits: quantityDigits,
			maximumFractionDigits: quantityDigits,
		});
	};
	return {
		total: {
			cellStyle: {
				fontWeight: 'bold',
				backgroundColor: globalStyles.colors.secondaryGray[300],
			},
			pinned: 'right',
			width: widthTotal,
			cellRenderer: 'agAnimateShowChangeCellRenderer',
			cellClass: 'number-cell',
			enableCellChangeFlash: true,
			valueFormatter,
		},
		number: {
			valueFormatter,
			valueSetter: params => {
				const key = params.colDef.field as string;
				const newValue = convertToNumber(
					params.newValue,
					quantityDigits
				);
				const oldValue = params.oldValue || 0;
				if (!params.data) return false;
				params.data[key] = newValue;
				params.data.total = params.data.total - oldValue + newValue;

				const rowNode = params.node as IRowNode<any>;

				params.data._flashMap ??= {};

				params.data._flashMap[key] =
					newValue > oldValue ? 'increase' : 'decrease';

				params.api.flashCells({
					columns: [key],
					rowNodes: [rowNode],
				});

				setTimeout(() => {
					if (params.data?._flashMap) {
						delete params.data._flashMap[key];
						params.api.refreshCells({
							columns: [key],
							rowNodes: [rowNode],
							force: true,
						});
					}
				}, 500);
				return true;
			},
			cellRenderer: 'agAnimateShowChangeCellRenderer',
			cellEditor: 'agNumberCellEditor',
			cellClassRules: {
				'cell-flash-green': params =>
					params.data?._flashMap?.[params.colDef.field as string] ===
					'increase',
				'cell-flash-red': params =>
					params.data?._flashMap?.[params.colDef.field as string] ===
					'decrease',
			},
			cellEditorParams: {
				precision: quantityDigits,
				min: 0,
			} as INumberCellEditorParams,
		},
		string: {},
	};
};
export const generateColumns = <Type,>({
	headers,
	quantityDigits,
	widthGrid,
	disabledEditCell,
	canEdit,
}: {
	headers: IHeader<Type>[];
	quantityDigits: number;
	widthGrid: number;
	disabledEditCell?: (colDef: ColDef, data: Type) => boolean;
	canEdit?: boolean;
}): ColDef[] => {
	const widthTotal = 200;
	const minWidth = 100;

	const formattingTypes = getSpecificColumnsByType(
		quantityDigits,
		widthTotal
	);
	const halfGridWidthExcludingTotal = (widthGrid - widthTotal) / 2;

	// -1 because the total column is not editable
	const quantityColumnsNotEditable =
		headers.reduce((acc, header) => acc + (header.editable ? 0 : 1), 0) - 1;

	const quantityColumnEditable =
		headers.length - quantityColumnsNotEditable - 1;

	const widthColumnNotEditable = Math.max(
		halfGridWidthExcludingTotal / quantityColumnsNotEditable,
		minWidth
	);
	const widthColumnEditable = Math.max(
		halfGridWidthExcludingTotal / quantityColumnEditable,
		minWidth
	);

	return headers.map(
		({
			field,
			headerName,
			cellRenderer,
			editable = false,
			type = 'string',
			cellStyle,
			sortable = true,
		}) => {
			return {
				field,
				headerName: headerName.toUpperCase(),
				cellStyle,
				sortable,
				minWidth: 100,
				maxWidth: 500,
				...(editable && canEdit
					? {
							editable: (
								params: EditableCallbackParams<Type>
							) => {
								const { colDef, data } = params;
								return disabledEditCell && data
									? disabledEditCell(colDef, data)
									: true;
							},
							width: widthColumnEditable,
					  }
					: {
							width: widthColumnNotEditable,
					  }),
				enablePivot: true,
				enableRowGroup: true,
				enableValue: true,
				colSpan: params => {
					const rowGroupColumns =
						params.api.getRowGroupColumns()?.length;
					if (params.data && params?.data?.['isHighlighted']) {
						const firstColumnVisible = params.api
							.getAllGridColumns()
							?.find(
								col =>
									col.isVisible() &&
									!col.isCellEditable(params.data)
							);
						if (
							(firstColumnVisible?.getColId() !==
								params.colDef.field &&
								rowGroupColumns === 0) ||
							params.colDef.cellDataType === 'number'
						) {
							return 1;
						}
						const quantityColumnsNotEditableVisible = params.api
							.getAllGridColumns()
							.reduce((acc, col) => {
								const result =
									col.getColDef().cellDataType !== 'number' &&
									col.isVisible() &&
									!col.getColDef().showRowGroup;
								return acc + (result ? 1 : 0);
							}, 0);
						return quantityColumnsNotEditableVisible;
					}
					return 1;
				},
				cellRenderer,
				...formattingTypes[type],
			};
		}
	);
};
